import { object, string, dict, constant, number, optional, union, boolean, array, lazy, } from '@mojotech/json-type-validation';
import { CustomCombinators } from './custom-combinators';
const { isValidComponentName, isValidFileName, isValidElementName, isValidNavLink, } = CustomCombinators;
export const referenceTypeDecoder = union(constant('prop'), constant('state'), constant('local'), constant('attr'), constant('children'), constant('token'));
export const dynamicValueDecoder = object({
    type: constant('dynamic'),
    content: object({
        referenceType: referenceTypeDecoder,
        id: string(),
    }),
});
export const staticValueDecoder = object({
    type: constant('static'),
    content: union(string(), number(), boolean(), array()),
});
export const styleSetMediaConditionDecoder = object({
    type: constant('screen-size'),
    meta: object({
        maxWidth: number(),
        maxHeight: optional(number()),
        minHeight: optional(number()),
        minWidth: optional(number()),
    }),
    content: dict(union(staticValueDecoder, string(), number(), lazy(() => tokenReferenceDecoder))),
});
export const styleSetStateConditionDecoder = object({
    type: constant('element-state'),
    meta: object({
        state: lazy(() => elementStateDecoder),
    }),
    content: dict(union(staticValueDecoder, string(), number(), lazy(() => tokenReferenceDecoder))),
});
export const projectStyleConditionsDecoder = union(styleSetMediaConditionDecoder, styleSetStateConditionDecoder);
export const tokenReferenceDecoder = object({
    type: constant('dynamic'),
    content: object({
        referenceType: constant('token'),
        id: string(),
    }),
});
export const styleSetDefinitionDecoder = object({
    id: string(),
    name: string(),
    type: constant('reusable-project-style-map'),
    conditions: optional(array(projectStyleConditionsDecoder)),
    content: dict(union(staticValueDecoder, string(), number(), tokenReferenceDecoder)),
});
// TODO: Implement decoder for () => void
export const stateOrPropDefinitionDecoder = union(string(), number(), boolean(), array(union(string(), number(), object())), object());
export const pageOptionsDecoder = object({
    componentName: optional(isValidComponentName()),
    navLink: optional(isValidNavLink()),
    fileName: optional(isValidFileName()),
});
export const globalAssetsValidator = object({
    type: union(constant('script'), constant('style'), constant('font'), constant('canonical'), constant('icon')),
    path: optional(string()),
    content: optional(string()),
    options: optional(object({
        async: optional(boolean()),
        defer: optional(boolean()),
        target: optional(string()),
        iconType: optional(string()),
        iconSizes: optional(string()),
    })),
});
export const componentSeoDecoder = object({
    title: optional(string()),
    metaTags: optional(array(dict(string()))),
    assets: optional(array(globalAssetsValidator)),
});
export const stateValueDetailsDecoder = object({
    value: union(string(), number(), boolean()),
    pageOptions: optional(pageOptionsDecoder),
    seo: optional(componentSeoDecoder),
});
export const propDefinitionsDecoder = object({
    type: union(constant('string'), constant('boolean'), constant('number'), constant('array'), constant('func'), constant('object'), constant('children')),
    defaultValue: stateOrPropDefinitionDecoder,
    isRequired: optional(boolean()),
});
export const stateDefinitionsDecoder = object({
    type: union(constant('string'), constant('boolean'), constant('number'), constant('array'), constant('func'), constant('object'), constant('children')),
    defaultValue: stateOrPropDefinitionDecoder,
    values: optional(array(stateValueDetailsDecoder)),
});
export const outputOptionsDecoder = object({
    componentClassName: optional(isValidComponentName()),
    fileName: optional(isValidFileName()),
    styleFileName: optional(isValidFileName()),
    templateFileName: optional(isValidFileName()),
    moduleName: optional(isValidFileName()),
    folderPath: optional(array(isValidFileName())),
});
export const peerDependencyDecoder = object({
    type: constant('package'),
    version: string(),
    path: string(),
});
export const externaldependencyDecoder = object({
    type: union(constant('library'), constant('package')),
    path: string(),
    version: string(),
    meta: optional(object({
        namedImport: optional(boolean()),
        originalName: optional(string()),
        importJustPath: optional(boolean()),
        useAsReference: optional(boolean()),
    })),
});
export const localDependencyDecoder = object({
    type: constant('local'),
    path: optional(string()),
    meta: optional(object({
        namedImport: optional(boolean()),
        originalName: optional(string()),
        importJustPath: optional(boolean()),
    })),
});
export const dependencyDecoder = union(localDependencyDecoder, externaldependencyDecoder);
export const importReferenceDecoder = object({
    type: constant('import'),
    content: object({
        id: string(),
    }),
});
export const attributeValueDecoder = union(dynamicValueDecoder, staticValueDecoder, importReferenceDecoder);
export const styleValueDecoder = union(staticValueDecoder, dynamicValueDecoder);
export const styleDefinitionsDecoder = dict(styleValueDecoder);
export const eventHandlerStatementDecoder = object({
    type: string(),
    calls: optional(string()),
    modifies: optional(string()),
    newState: optional(union(string(), number(), boolean())),
    args: optional(array(union(string(), number(), boolean()))),
});
export const urlLinkNodeDecoder = object({
    type: constant('url'),
    content: object({
        url: union(attributeValueDecoder, string()),
        newTab: boolean(),
    }),
});
export const sectionLinkNodeDecoder = object({
    type: constant('section'),
    content: dict(string()),
});
export const navLinkNodeDecoder = object({
    type: constant('navlink'),
    content: object({
        routeName: string(),
    }),
});
export const uidlMailLinkNodeDecoder = object({
    type: constant('mail'),
    content: object({
        mail: optional(string()),
        subject: optional(string()),
        body: optional(string()),
    }),
});
export const phoneLinkNodeDecoder = object({
    type: constant('phone'),
    content: object({
        phone: string(),
    }),
});
export const uidlLinkNodeDecoder = union(urlLinkNodeDecoder, sectionLinkNodeDecoder, navLinkNodeDecoder, uidlMailLinkNodeDecoder, phoneLinkNodeDecoder);
export const rawValueDecoder = object({
    type: constant('raw'),
    content: string(),
});
export const elementStateDecoder = union(constant('hover'), constant('active'), constant('focus'), constant('disabled'));
export const elementStyleWithStateConditionDecoder = object({
    conditionType: constant('element-state'),
    content: elementStateDecoder,
});
export const elementStyleWithMediaConditionDecoder = object({
    conditionType: constant('screen-size'),
    minHeight: optional(number()),
    maxHeight: optional(number()),
    minWidth: optional(number()),
    maxWidth: number(),
});
export const styleConditionsDecoder = union(elementStyleWithMediaConditionDecoder, elementStyleWithStateConditionDecoder);
export const elementProjectReferencedStyle = object({
    id: string(),
    type: constant('style-map'),
    content: object({
        mapType: constant('project-referenced'),
        conditions: optional(array(styleConditionsDecoder)),
        referenceId: string(),
    }),
});
export const elementInlineReferencedStyle = object({
    id: string(),
    type: constant('style-map'),
    content: object({
        mapType: constant('inlined'),
        conditions: array(styleConditionsDecoder),
        styles: optional(dict(union(attributeValueDecoder, string(), number()))),
    }),
});
export const designTokensDecoder = dict(union(staticValueDecoder, string(), number()));
export const elementDecoder = object({
    elementType: string(),
    semanticType: optional(string()),
    name: optional(isValidElementName()),
    key: optional(string()),
    dependency: optional(dependencyDecoder),
    style: optional(dict(union(attributeValueDecoder, string(), number()))),
    attrs: optional(dict(union(attributeValueDecoder, string(), number()))),
    events: optional(dict(array(eventHandlerStatementDecoder))),
    abilities: optional(object({
        link: optional(uidlLinkNodeDecoder),
    })),
    children: optional(array(lazy(() => uidlNodeDecoder))),
    referencedStyles: optional(dict(union(elementInlineReferencedStyle, elementProjectReferencedStyle))),
    selfClosing: optional(boolean()),
    ignore: optional(boolean()),
});
export const slotNodeDecoder = object({
    type: constant('slot'),
    content: union(object({
        name: optional(string()),
        fallback: optional(union(staticValueDecoder, dynamicValueDecoder, lazy(() => elementNodeDecoder))),
    }), object({})),
});
export const repeatNodeDecoder = object({
    type: constant('repeat'),
    content: object({
        node: lazy(() => elementNodeDecoder),
        dataSource: optional(attributeValueDecoder),
        meta: optional(object({
            useIndex: optional(boolean()),
            iteratorName: optional(string()),
            dataSourceIdentifier: optional(string()),
            iteratorKey: optional(string()),
        })),
    }),
});
export const conditionalNodeDecoder = object({
    type: constant('conditional'),
    content: object({
        node: lazy(() => uidlNodeDecoder),
        reference: dynamicValueDecoder,
        value: union(string(), number(), boolean()),
        condition: optional(object({
            conditions: array(object({ operation: string(), operand: optional(union(string(), number(), boolean())) })),
            matchingCriteria: optional(string()),
        })),
    }),
});
export const elementNodeDecoder = object({
    type: constant('element'),
    content: elementDecoder,
});
export const uidlNodeDecoder = union(elementNodeDecoder, dynamicValueDecoder, staticValueDecoder, rawValueDecoder, conditionalNodeDecoder, repeatNodeDecoder, slotNodeDecoder, string());
//# sourceMappingURL=utils.js.map